'use client';

import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { HateoasTarget, Tool } from '@repo/contracts';

import { useAIStateBinding } from '@context/AiContext';

type HalLinkLike = {
    href?: string;
    method?: string;
    title?: string;
    label?: string;
    chatbotHidden?: boolean;
    tool?: Tool;
};

type LegacyLink = {
    rel?: string;
    href?: string;
    method?: string;
    title?: string;
};

export interface HateoasScopeState {
    links: Record<string, HateoasTarget>;
    modules: Record<string, Record<string, HateoasTarget>>;
    actions: Record<string, HateoasTarget>;
    meta?: {
        sourceEndpoint?: string;
        updatedAt: number;
    };
}

interface NormalizedPayload {
    links: Record<string, HateoasTarget>;
    modules: Record<string, Record<string, HateoasTarget>>;
    actions: Record<string, HateoasTarget>;
}

interface HateoasContextType {
    links: Record<string, HateoasTarget>;
    modules: Record<string, Record<string, HateoasTarget>>;
    actions: Record<string, HateoasTarget>;
    scopes: Record<string, HateoasScopeState>;
    getScopes: () => Record<string, HateoasScopeState>;
    isLoading: boolean;
    setScope: (scopeKey: string, payload: unknown, options?: { sourceEndpoint?: string }) => void;
    mergeScope: (scopeKey: string, payload: unknown, options?: { sourceEndpoint?: string }) => void;
    clearScope: (scopeKey: string) => void;
    clearAll: () => void;
    getRel: (rel: string, scopePriority?: string[]) => HateoasTarget | null;
    getScopeLinks: (scopeKey: string) => Record<string, HateoasTarget>;
    getModuleRel: (moduleKey: string, rel: string, scopePriority?: string[]) => HateoasTarget | null;
    hasRel: (rel: string, scopePriority?: string[]) => boolean;
    hasModule: (moduleKey: string, scopePriority?: string[]) => boolean;
    updateLocalActions: (newActions?: Record<string, HateoasTarget>, newLinks?: Record<string, HateoasTarget>) => void;
    clearLocalActions: () => void;
    clearCache: () => void;
    loadGlobalActions: (forceRefresh?: boolean) => Promise<void>;
}

const RUNTIME_SCOPE_KEY = '__runtime__';

const HateoasContext = createContext<HateoasContextType | null>(null);

const normalizeMethod = (method?: string): HateoasTarget['method'] => {
    const candidate = (method || 'GET').toUpperCase();
    if (candidate === 'POST' || candidate === 'PUT' || candidate === 'PATCH' || candidate === 'DELETE') {
        return candidate;
    }
    return 'GET';
};

const linkToAction = (link: HalLinkLike | undefined): HateoasTarget | null => {
    if (!link?.href) {
        return null;
    }

    return {
        href: link.href,
        method: normalizeMethod(link.method),
        label: link.label || link.title,
        chatbotHidden: link.chatbotHidden,
        tool: link.tool,
    };
};

const normalizeLinksRecord = (links: Record<string, HalLinkLike> | null | undefined): Record<string, HateoasTarget> => {
    if (!links || typeof links !== 'object') {
        return {};
    }

    return Object.entries(links).reduce<Record<string, HateoasTarget>>((acc, [rel, link]) => {
        const action = linkToAction(link);
        if (!action) {
            return acc;
        }
        acc[rel] = action;
        return acc;
    }, {});
};

const normalizeLegacyLinksArray = (links: LegacyLink[] | null | undefined): Record<string, HateoasTarget> => {
    if (!Array.isArray(links)) {
        return {};
    }

    return links.reduce<Record<string, HateoasTarget>>((acc, link) => {
        if (!link?.rel || !link?.href) {
            return acc;
        }
        acc[link.rel] = {
            href: link.href,
            method: normalizeMethod(link.method),
            label: link.title,
        };
        return acc;
    }, {});
};

const normalizeModules = (modules: Record<string, HalLinkLike> | null | undefined): Record<string, Record<string, HateoasTarget>> => {
    if (!modules || typeof modules !== 'object') {
        return {};
    }

    return Object.entries(modules).reduce<Record<string, Record<string, HateoasTarget>>>((acc, [moduleKey, moduleRootLink]) => {
        const moduleRoot = linkToAction(moduleRootLink);
        if (!moduleRoot) {
            return acc;
        }

        acc[moduleKey] = {
            getModuleRoot: moduleRoot,
        };
        return acc;
    }, {});
};

const normalizePayload = (payload: unknown): NormalizedPayload => {
    if (!payload || typeof payload !== 'object') {
        return { links: {}, modules: {}, actions: {} };
    }

    const direct = payload as {
        _links?: Record<string, HalLinkLike>;
        links?: Record<string, HalLinkLike>;
        _embedded?: { modules?: Record<string, HalLinkLike> };
        _actions?: Record<string, HalLinkLike>;
        actions?: Record<string, HalLinkLike>;
        data?: unknown;
    };

    const nestedData = direct.data && typeof direct.data === 'object' ? direct.data as typeof direct : null;
    const linksFromTopLevel = normalizeLinksRecord(direct.links || direct._links);
    const linksFromNestedData = normalizeLinksRecord(nestedData?.links || nestedData?._links);
    const linksFromLegacyArray = normalizeLegacyLinksArray(payload as LegacyLink[]);

    const modulesFromTopLevel = normalizeModules(direct._embedded?.modules);
    const modulesFromNestedData = normalizeModules(nestedData?._embedded?.modules);

    const actionsFromTopLevel = normalizeLinksRecord(direct.actions || direct._actions);
    const actionsFromNestedData = normalizeLinksRecord(nestedData?.actions || nestedData?._actions);

    return {
        links: {
            ...linksFromTopLevel,
            ...linksFromNestedData,
            ...linksFromLegacyArray,
        },
        modules: mergeModules(modulesFromTopLevel, modulesFromNestedData),
        actions: {
            ...actionsFromTopLevel,
            ...actionsFromNestedData,
        },
    };
};

const mergeModules = (
    left: Record<string, Record<string, HateoasTarget>>,
    right: Record<string, Record<string, HateoasTarget>>,
) => {
    const merged = { ...left };
    Object.entries(right).forEach(([moduleKey, moduleLinks]) => {
        merged[moduleKey] = {
            ...(merged[moduleKey] || {}),
            ...moduleLinks,
        };
    });
    return merged;
};

export function HateoasProvider({ children }: { children: ReactNode }) {
    const [scopes, setScopes] = useState<Record<string, HateoasScopeState>>({});
    const scopesRef = useRef<Record<string, HateoasScopeState>>({});
    const scopeOrderRef = useRef<string[]>([]);

    const upsertScopeOrder = useCallback((scopeKey: string) => {
        if (!scopeOrderRef.current.includes(scopeKey)) {
            scopeOrderRef.current = [...scopeOrderRef.current, scopeKey];
        }
    }, []);

    const setScope = useCallback((scopeKey: string, payload: unknown, options?: { sourceEndpoint?: string }) => {
        if (!scopeKey) {
            return;
        }

        const normalized = normalizePayload(payload);
        upsertScopeOrder(scopeKey);

        const newState = {
            ...scopesRef.current,
            [scopeKey]: {
                links: normalized.links,
                modules: normalized.modules,
                actions: normalized.actions,
                meta: {
                    sourceEndpoint: options?.sourceEndpoint,
                    updatedAt: Date.now(),
                },
            },
        };
        scopesRef.current = newState;
        setScopes(newState);
    }, []);

    const mergeScope = useCallback((scopeKey: string, payload: unknown, options?: { sourceEndpoint?: string }) => {
        if (!scopeKey) {
            return;
        }

        const normalized = normalizePayload(payload);
        upsertScopeOrder(scopeKey);

        const prev = scopesRef.current;
        const current = prev[scopeKey] || { links: {}, modules: {}, actions: {} };
        const newState = {
            ...prev,
            [scopeKey]: {
                links: {
                    ...current.links,
                    ...normalized.links,
                },
                modules: mergeModules(current.modules, normalized.modules),
                actions: {
                    ...current.actions,
                    ...normalized.actions,
                },
                meta: {
                    sourceEndpoint: options?.sourceEndpoint,
                    updatedAt: Date.now(),
                },
            },
        };
        scopesRef.current = newState;
        setScopes(newState);
    }, []);

    const clearScope = useCallback((scopeKey: string) => {
        scopeOrderRef.current = scopeOrderRef.current.filter((scope) => scope !== scopeKey);
        const prev = scopesRef.current;
        const { [scopeKey]: _, ...rest } = prev;
        scopesRef.current = rest;
        setScopes(rest);
    }, []);

    const clearAll = useCallback(() => {
        scopeOrderRef.current = [];
        scopesRef.current = {};
        setScopes({});
    }, []);

    const links = useMemo(() => {
        return scopeOrderRef.current.reduce<Record<string, HateoasTarget>>((acc, scopeKey) => {
            const scope = scopes[scopeKey];
            if (!scope) {
                return acc;
            }
            return {
                ...acc,
                ...scope.links,
            };
        }, {});
    }, [scopes]);

    const modules = useMemo(() => {
        return scopeOrderRef.current.reduce<Record<string, Record<string, HateoasTarget>>>((acc, scopeKey) => {
            const scope = scopes[scopeKey];
            if (!scope) {
                return acc;
            }
            return mergeModules(acc, scope.modules);
        }, {});
    }, [scopes]);

    const actions = useMemo(() => {
        return scopeOrderRef.current.reduce<Record<string, HateoasTarget>>((acc, scopeKey) => {
            const scope = scopes[scopeKey];
            if (!scope) {
                return acc;
            }

            const moduleActions = Object.entries(scope.modules || {}).reduce<Record<string, HateoasTarget>>((modAcc, [modKey, modLinks]) => {
                if (modLinks.getModuleRoot) {
                    const actionName = modLinks.getModuleRoot.tool?.name || `discover_${modKey}`;
                    modAcc[actionName] = modLinks.getModuleRoot;
                }
                return modAcc;
            }, {});

            return {
                ...acc,
                ...scope.links,
                ...scope.actions,
                ...moduleActions,
            };
        }, {});
    }, [scopes]);

    const getRel = useCallback((rel: string, scopePriority?: string[]): HateoasTarget | null => {
        if (!rel) {
            return null;
        }

        const scanOrder = scopePriority && scopePriority.length > 0
            ? scopePriority
            : [...scopeOrderRef.current].reverse();

        for (const scopeKey of scanOrder) {
            const candidate = scopes[scopeKey]?.links?.[rel];
            if (candidate) {
                return candidate;
            }
        }

        return null;
    }, [scopes]);

    const getModuleRel = useCallback((moduleKey: string, rel: string, scopePriority?: string[]): HateoasTarget | null => {
        if (!moduleKey || !rel) {
            return null;
        }

        const scanOrder = scopePriority && scopePriority.length > 0
            ? scopePriority
            : [...scopeOrderRef.current].reverse();

        for (const scopeKey of scanOrder) {
            const candidate = scopes[scopeKey]?.modules?.[moduleKey]?.[rel];
            if (candidate) {
                return candidate;
            }
        }

        return null;
    }, [scopes]);

    const getScopeLinks = useCallback((scopeKey: string): Record<string, HateoasTarget> => {
        return scopes[scopeKey]?.links || {};
    }, [scopes]);

    const hasRel = useCallback((rel: string, scopePriority?: string[]) => !!getRel(rel, scopePriority), [getRel]);
    const hasModule = useCallback((moduleKey: string, scopePriority?: string[]) => {
        const scanOrder = scopePriority && scopePriority.length > 0
            ? scopePriority
            : [...scopeOrderRef.current].reverse();

        for (const scopeKey of scanOrder) {
            if (scopes[scopeKey]?.modules?.[moduleKey]) {
                return true;
            }
        }

        return false;
    }, [scopes]);

    const updateLocalActions = useCallback((newActions?: Record<string, HateoasTarget>, newLinks?: Record<string, HateoasTarget>) => {
        setScope(RUNTIME_SCOPE_KEY, { actions: newActions || {}, links: newLinks || {} }, { sourceEndpoint: 'runtime' });
    }, [setScope]);

    const clearLocalActions = useCallback(() => {
        clearScope(RUNTIME_SCOPE_KEY);
    }, [clearScope]);

    const clearCache = useCallback(() => {
        clearScope(RUNTIME_SCOPE_KEY);
    }, [clearScope]);

    const loadGlobalActions = useCallback(async (_forceRefresh?: boolean) => {
        return;
    }, []);

    useAIStateBinding('api-actions', undefined, actions);

    const getScopes = useCallback(() => scopesRef.current, []);

    const value = useMemo<HateoasContextType>(() => ({
        links,
        modules,
        actions,
        scopes,
        getScopes,
        isLoading: false,
        setScope,
        mergeScope,
        clearScope,
        clearAll,
        getRel,
        getScopeLinks,
        getModuleRel,
        hasRel,
        hasModule,
        updateLocalActions,
        clearLocalActions,
        clearCache,
        loadGlobalActions,
    }), [
        links,
        modules,
        actions,
        scopes,
        getScopes,
        setScope,
        mergeScope,
        clearScope,
        clearAll,
        getRel,
        getScopeLinks,
        getModuleRel,
        hasRel,
        hasModule,
        updateLocalActions,
        clearLocalActions,
        clearCache,
        loadGlobalActions,
    ]);

    return (
        <HateoasContext.Provider value={value}>
            {children}
        </HateoasContext.Provider>
    );
}

export const useHateoasContext = () => {
    const context = useContext(HateoasContext);
    if (!context) throw new Error('useHateoasContext must be used within HateoasProvider');
    return context;
};
